import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CreditCard, CheckCircle2, AlertTriangle, ArrowRight, Loader2, RefreshCw, ExternalLink, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import useSEO from "../lib/useSEO";
import { useAuth } from "../contexts/AuthContext";
import { PERIOD_OPTIONS, exportQuery, periodRange } from "../lib/periods";
import RevenueChart from "../components/RevenueChart";

const SELLER_ROLES = ["organizer", "venue_owner", "provider"];

const STATUS_FR = {
  paid: { label: "Encaissé", cls: "bg-emerald-100 text-emerald-700" },
  refunded: { label: "Remboursé", cls: "bg-ink/10 text-ink/50" },
  partially_refunded: { label: "Remb. partiel", cls: "bg-amber-100 text-amber-700" },
};

function RevenueStat({ label, value, accent }) {
  return (
    <div className="bg-white border border-ink/10 rounded-md p-5">
      <p className="text-label text-muted-foreground mb-1">{label}</p>
      <p className={`font-heading text-2xl ${accent ? "text-accent" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function RevenueSection() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  useEffect(() => {
    api.get("/my/sales").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const { stats, balance, sales } = data;

  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/sales/export.csv${exportQuery(period)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const [s, e] = periodRange(period);
      a.download = s
        ? `ventes-eventmarketpro-${s}-au-${e}.csv`
        : `ventes-eventmarketpro-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  return (
    <div className="mb-8" data-testid="revenue-section">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-label text-muted-foreground">Mes revenus</p>
        {sales.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              data-testid="export-period-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-ink/15 bg-white text-sm px-2 py-1.5 focus:outline-none focus:border-ink"
            >
              {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <Button data-testid="export-sales-csv-btn" variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> Exporter CSV
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <RevenueStat label="Ventes encaissées" value={`${stats.gross.toFixed(2)}€`} />
        <RevenueStat label="Commission (20%)" value={`−${stats.commission.toFixed(2)}€`} />
        <RevenueStat label="Net reversé" value={`${stats.net.toFixed(2)}€`} accent />
        <RevenueStat
          label="Solde Stripe"
          value={balance ? `${(balance.available + balance.pending).toFixed(2)}€` : "—"}
        />
      </div>
      {stats.sales_count > 0 && (
        <div className="mb-4">
          <RevenueChart
            testId="seller-revenue-chart"
            data={data.monthly}
            series={[
              { key: "gross", name: "Ventes encaissées", color: "#18181A" },
              { key: "net", name: "Net reversé", color: "#A07E45" },
            ]}
          />
        </div>
      )}
      {balance && (
        <p className="text-xs text-muted-foreground mb-4" data-testid="revenue-balance-detail">
          Solde Stripe : {balance.available.toFixed(2)}€ disponibles · {balance.pending.toFixed(2)}€ en cours de virement
        </p>
      )}
      {sales.length > 0 && (
        <div className="bg-white border border-ink/10 rounded-md divide-y divide-ink/10">
          {sales.map((s) => (
            <div key={s.id} className="p-4 flex items-center justify-between gap-4" data-testid={`sale-row-${s.id}`}>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-ink truncate">{s.item_title}</p>
                <p className="text-xs text-muted-foreground">
                  {s.buyer_name} · {s.paid_at ? new Date(s.paid_at).toLocaleDateString("fr-FR") : "—"}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className={`text-right ${s.payment_status === "refunded" ? "opacity-50" : ""}`}>
                  <p className={`font-bold text-sm text-ink ${s.payment_status === "refunded" ? "line-through" : ""}`}>{s.amount.toFixed(2)}€</p>
                  <p className={`text-xs text-trust ${s.payment_status === "refunded" ? "line-through" : ""}`}>Net : {s.seller_amount.toFixed(2)}€</p>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded ${(STATUS_FR[s.payment_status] || STATUS_FR.paid).cls}`}>
                  {(STATUS_FR[s.payment_status] || STATUS_FR.paid).label}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      {sales.length === 0 && (
        <p className="text-sm text-muted-foreground bg-white border border-ink/10 rounded-md p-6" data-testid="revenue-empty">
          Aucune vente encaissée pour le moment. Vos ventes apparaîtront ici dès le premier paiement.
        </p>
      )}
    </div>
  );
}

function StatBadge({ enabled, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${
          enabled ? "bg-emerald-100 text-emerald-700" : "bg-ink/10 text-ink/40"
        }`}
      >
        {enabled ? <CheckCircle2 className="h-4 w-4" /> : "—"}
      </span>
      <span className={enabled ? "text-ink" : "text-ink/50"}>{label}</span>
    </div>
  );
}

export default function StripeConnect() {
  useSEO({
    title: "Activer mes paiements",
    description: "Configurez Stripe Connect pour recevoir les paiements de vos ventes sur EventMarket Pro.",
  });

  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stripe/connect/status");
      setStatus(data);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de récupérer le statut Stripe.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh on return from Stripe onboarding
  useEffect(() => {
    if (searchParams.get("done") === "1") {
      toast.success("Retour de Stripe — nous rafraîchissons votre statut.");
      refresh();
    }
  }, [searchParams, refresh]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/stripe/connect/onboard");
      window.location.href = data.onboarding_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible de démarrer l'activation Stripe.";
      toast.error(msg);
      setStarting(false);
    }
  };

  const refreshLink = async () => {
    setStarting(true);
    try {
      const { data } = await api.get("/stripe/connect/refresh-link");
      window.location.href = data.onboarding_url;
    } catch (e) {
      toast.error("Impossible de régénérer le lien Stripe.");
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-cream-50 min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      </div>
    );
  }

  // Not eligible (visitor / exhibitor / admin)
  if (!SELLER_ROLES.includes(user?.role)) {
    return (
      <div className="bg-cream-50 min-h-[calc(100vh-4rem)]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <p className="text-label text-muted-foreground mb-2">Espace vendeur</p>
          <h1 className="font-heading font-light tracking-editorial text-3xl text-ink mb-4">
            Configuration Stripe non disponible
          </h1>
          <p className="text-ink/70">
            Cette page est réservée aux organisateurs d&apos;événements, propriétaires
            de lieux et prestataires. Votre rôle actuel ({user?.role || "?"}) ne nécessite pas
            de compte Stripe Connect.
          </p>
        </div>
      </div>
    );
  }

  const isOnboarded = status?.onboarded;
  const isReady = status?.ready_to_sell;
  const isPending = isOnboarded && !isReady;

  return (
    <div className="bg-cream-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="h-5 w-5 text-accent" />
          <p className="text-label text-muted-foreground">Paiements</p>
        </div>
        <h1
          data-testid="stripe-connect-title"
          className="font-heading font-light tracking-editorial text-4xl sm:text-5xl text-ink mb-4 leading-tight"
        >
          Activer mes<br />
          <span className="font-display italic text-accent">paiements en ligne.</span>
        </h1>
        <p className="text-ink/70 font-light max-w-2xl mb-12 leading-relaxed">
          Pour recevoir les paiements de vos exposants (ou clients pour un lieu / une prestation),
          vous devez créer un compte Stripe Connect. C&apos;est gratuit, sécurisé (PCI-DSS niveau 1),
          et prend environ 5 minutes.
        </p>

        {/* Status card */}
        <div className="bg-white border border-ink/10 rounded-md p-8 mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="text-label text-muted-foreground mb-2">Statut de votre compte</p>
              <h2 className="font-heading text-2xl text-ink font-light">
                {isReady ? (
                  <span className="text-emerald-700">✓ Compte actif</span>
                ) : isPending ? (
                  <span className="text-amber-700">⏳ Onboarding en cours</span>
                ) : isOnboarded ? (
                  <span className="text-amber-700">Onboarding incomplet</span>
                ) : (
                  <span className="text-ink/60">Non configuré</span>
                )}
              </h2>
            </div>
            <Button
              variant="ghost"
              onClick={refresh}
              disabled={loading}
              data-testid="stripe-refresh-status"
              className="text-ink/60 hover:text-ink"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" /> Actualiser
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatBadge enabled={status?.details_submitted} label="Identité vérifiée" />
            <StatBadge enabled={status?.charges_enabled} label="Encaissements activés" />
            <StatBadge enabled={status?.payouts_enabled} label="Virements activés" />
          </div>

          {status?.requirements_due?.length > 0 && (
            <div data-testid="stripe-req-due" className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">
                  <p className="font-semibold mb-1">Informations manquantes ({status.requirements_due.length})</p>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    {status.requirements_due.slice(0, 6).map((r) => (
                      <li key={r} className="font-mono">{r}</li>
                    ))}
                    {status.requirements_due.length > 6 && (
                      <li className="italic">…et {status.requirements_due.length - 6} de plus</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {status?.disabled_reason && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <p className="font-semibold mb-1">Compte suspendu par Stripe</p>
                  <p className="text-xs font-mono">{status.disabled_reason}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action button */}
          {!isOnboarded && (
            <Button
              onClick={startOnboarding}
              disabled={starting}
              data-testid="stripe-onboard-btn"
              className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none py-6 text-sm font-semibold tracking-wide"
            >
              {starting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection vers Stripe…</>
              ) : (
                <>Activer mes paiements <ArrowRight className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          )}
          {isOnboarded && !isReady && (
            <Button
              onClick={refreshLink}
              disabled={starting}
              data-testid="stripe-continue-btn"
              className="w-full bg-accent hover:bg-accent/90 text-ink rounded-none py-6 text-sm font-semibold tracking-wide"
            >
              {starting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirection vers Stripe…</>
              ) : (
                <>Reprendre l&apos;onboarding <ExternalLink className="h-4 w-4 ml-2" /></>
              )}
            </Button>
          )}
          {isReady && (
            <div data-testid="stripe-ready-banner" className="bg-emerald-50 border-l-4 border-emerald-400 p-4 rounded">
              <p className="text-sm text-emerald-900">
                <strong>Tout est prêt !</strong> Vous pouvez recevoir des paiements. Une commission
                de 20% est prélevée par EventMarket Pro sur chaque transaction, le solde vous est
                reversé automatiquement selon le calendrier Stripe (2 à 7 jours ouvrés).
              </p>
            </div>
          )}
        </div>

        {/* Revenue summary — only when the account can sell */}
        {isReady && <RevenueSection />}

        {/* How it works */}
        <div className="bg-ink text-cream rounded-md p-8 grain">
          <p className="text-label text-accent mb-3">Comment ça marche</p>
          <h3 className="font-heading text-2xl font-light tracking-editorial mb-6">
            En 3 étapes simples
          </h3>
          <ol className="space-y-4 text-sm text-cream/85 font-light leading-relaxed">
            <li className="flex gap-4">
              <span className="text-accent font-bold text-xl leading-none">1.</span>
              <span>
                <strong className="text-cream">Cliquez « Activer mes paiements »</strong> — vous êtes
                redirigé vers un formulaire Stripe hébergé (sécurisé, en français).
              </span>
            </li>
            <li className="flex gap-4">
              <span className="text-accent font-bold text-xl leading-none">2.</span>
              <span>
                <strong className="text-cream">Complétez les informations légales</strong> — SIRET,
                RIB, pièce d&apos;identité du dirigeant, justificatif de domicile. Stripe vérifie
                votre identité en 5 à 30 minutes.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="text-accent font-bold text-xl leading-none">3.</span>
              <span>
                <strong className="text-cream">Revenez sur EventMarket Pro</strong> — vous pouvez
                désormais recevoir des paiements. 80% du montant vous est reversé, 20% est notre
                commission plateforme.
              </span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
